import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Treatment } from '@/lib/types';
import { ntd, dt } from '@/lib/format';
import {
  Banner,
  Card,
  Empty,
  Field,
  PageHeader,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { useAuth } from '@/store/auth';

interface WaitItem { id: string; customerId: string; treatmentId: string | null; note: string; status: string; createdAt: string }
interface QuoteRow { id: string; customerId: string; items: { name: string; qty: number; unitPriceCents: number }[]; subtotalCents: number; discountCents: number; totalCents: number; status: string; createdAt: string }
interface Plan { id: string; name: string; priceCents: number; intervalDays: number; benefits: string }
interface Sub { id: string; customerId: string; planId: string; status: string; nextBillingAt: string | null }

const QSTATUS: Record<string, Parameters<typeof Tag>[0]['tone']> = { draft: 'default', sent: 'primary', accepted: 'success', declined: 'danger', expired: 'warn' };

export function ClinicToolsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const { msg, ok, fail } = useToast();

  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const waitlist = useQuery({ queryKey: ['waitlist'], queryFn: () => api.get<WaitItem[]>('/growth/waitlist') });
  const quotes = useQuery({ queryKey: ['quotes'], queryFn: () => api.get<QuoteRow[]>('/growth/quotes') });
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => api.get<Plan[]>('/growth/plans') });
  const subs = useQuery({ queryKey: ['subs'], queryFn: () => api.get<Sub[]>('/growth/subscriptions') });
  const txName = (id: string | null) => (id ? treatments.data?.find((t) => t.id === id)?.name ?? '—' : '—');
  const inval = (k: string) => qc.invalidateQueries({ queryKey: [k] });

  // 候補
  const [wCust, setWCust] = useState(''); const [wTx, setWTx] = useState(''); const [wNote, setWNote] = useState('');
  const addWait = useMutation({ mutationFn: () => api.post('/growth/waitlist', { customerId: wCust, treatmentId: wTx || undefined, note: wNote }), onSuccess: () => { ok('已加入候補'); setWCust(''); setWNote(''); void inval('waitlist'); }, onError: fail });
  const setWait = useMutation({ mutationFn: (v: { id: string; status: string }) => api.patch(`/growth/waitlist/${v.id}/status`, { status: v.status }), onSuccess: () => { ok('已更新'); void inval('waitlist'); }, onError: fail });

  // 報價
  const [qCust, setQCust] = useState(''); const [qItems, setQItems] = useState<{ name: string; qty: number; unitPrice: number }[]>([{ name: '', qty: 1, unitPrice: 0 }]); const [qDiscount, setQDiscount] = useState(0);
  const qSubtotal = qItems.reduce((s, i) => s + i.qty * i.unitPrice, 0);
  const addQuote = useMutation({
    mutationFn: () => api.post('/growth/quotes', { customerId: qCust, items: qItems.filter((i) => i.name).map((i) => ({ name: i.name, qty: i.qty, unitPriceCents: Math.round(i.unitPrice * 100) })), discountCents: Math.round(qDiscount * 100) }),
    onSuccess: () => { ok('報價單已建立'); setQItems([{ name: '', qty: 1, unitPrice: 0 }]); setQDiscount(0); void inval('quotes'); }, onError: fail });
  const setQuote = useMutation({ mutationFn: (v: { id: string; status: string }) => api.patch(`/growth/quotes/${v.id}/status`, { status: v.status }), onSuccess: () => { void inval('quotes'); }, onError: fail });

  // 會員
  const [pName, setPName] = useState(''); const [pPrice, setPPrice] = useState(0); const [pInterval, setPInterval] = useState(30); const [pBenefits, setPBenefits] = useState('');
  const addPlan = useMutation({ mutationFn: () => api.post('/growth/plans', { name: pName, priceCents: Math.round(pPrice * 100), intervalDays: pInterval, benefits: pBenefits }), onSuccess: () => { ok('方案已建立'); setPName(''); setPPrice(0); setPBenefits(''); void inval('plans'); }, onError: fail });
  const [sCust, setSCust] = useState(''); const [sPlan, setSPlan] = useState('');
  const subscribe = useMutation({ mutationFn: () => api.post('/growth/subscriptions', { customerId: sCust, planId: sPlan }), onSuccess: () => { ok('已建立訂閱'); setSCust(''); void inval('subs'); }, onError: fail });
  const setSub = useMutation({ mutationFn: (v: { id: string; status: string }) => api.patch(`/growth/subscriptions/${v.id}/status`, { status: v.status }), onSuccess: () => { void inval('subs'); }, onError: fail });

  return (
    <div>
      <PageHeader title="診所業務工具" subtitle="候補名單 · 諮詢報價單 · 會員訂閱制" />
      <Banner msg={msg} />

      {/* 候補名單 */}
      <Card title="候補名單" icon="⏳">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <div style={{ width: 230 }}><CustomerPicker value={wCust} onChange={setWCust} /></div>
          <Select value={wTx} onChange={setWTx} placeholder="療程(選填)" options={[{ value: '', label: '不指定療程' }, ...(treatments.data ?? []).map((t) => ({ value: t.id, label: t.name }))]} />
          <input style={{ ...inputStyle, width: 180 }} placeholder="備註(如:指定週末)" value={wNote} onChange={(e) => setWNote(e.target.value)} />
          <button className="btn btn-primary" disabled={!wCust || addWait.isPending} onClick={() => addWait.mutate()}>加入候補</button>
        </div>
        {!waitlist.data?.length ? <Empty>目前無候補。</Empty> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>療程</th><th style={thStyle}>備註</th><th style={thStyle}>狀態</th><th style={thStyle}>加入</th><th style={thStyle}></th></tr></thead>
            <tbody>{waitlist.data.map((w) => (
              <tr key={w.id}>
                <td style={cellStyle}>{txName(w.treatmentId)}</td>
                <td style={cellStyle}>{w.note || '—'}</td>
                <td style={cellStyle}><Tag tone={w.status === 'notified' ? 'warn' : 'default'}>{w.status === 'notified' ? '已通知' : '等待中'}</Tag></td>
                <td style={cellStyle} className="num">{w.createdAt.slice(5, 10)}</td>
                <td style={cellStyle}>
                  <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setWait.mutate({ id: w.id, status: 'notified' })}>標記已通知</button>
                  <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setWait.mutate({ id: w.id, status: 'booked' })}>已預約</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </Card>

      {/* 諮詢報價單 */}
      <Card title="諮詢報價單 / 療程計畫" icon="📝">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <div style={{ width: 230 }}><CustomerPicker value={qCust} onChange={setQCust} /></div>
        </div>
        {qItems.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <input style={{ ...inputStyle, flex: 2 }} placeholder="項目(如:皮秒雷射 5 次)" value={it.name} onChange={(e) => setQItems((s) => s.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
            <input type="number" style={{ ...inputStyle, width: 70 }} value={it.qty} onChange={(e) => setQItems((s) => s.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x))} />
            <input type="number" style={{ ...inputStyle, width: 110 }} placeholder="單價" value={it.unitPrice || ''} onChange={(e) => setQItems((s) => s.map((x, j) => j === i ? { ...x, unitPrice: Number(e.target.value) } : x))} />
            <span style={{ fontSize: 12, color: 'var(--ink-3)', width: 90 }} className="num">{ntd(it.qty * it.unitPrice * 100)}</span>
            {qItems.length > 1 && <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setQItems((s) => s.filter((_, j) => j !== i))}>移除</button>}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setQItems((s) => [...s, { name: '', qty: 1, unitPrice: 0 }])}>+ 加一項</button>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>小計 {ntd(qSubtotal * 100)}</span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>折扣 NT$</span>
          <input type="number" style={{ ...inputStyle, width: 90 }} value={qDiscount || ''} onChange={(e) => setQDiscount(Number(e.target.value))} />
          <b style={{ fontSize: 13 }}>總額 {ntd(Math.max(0, qSubtotal - qDiscount) * 100)}</b>
          <button className="btn btn-primary" disabled={!qCust || qSubtotal === 0 || addQuote.isPending} onClick={() => addQuote.mutate()}>建立報價單</button>
        </div>
        {!!quotes.data?.length && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
            <thead><tr><th style={thStyle}>項目數</th><th style={thStyle}>總額</th><th style={thStyle}>狀態</th><th style={thStyle}>建立</th><th style={thStyle}></th></tr></thead>
            <tbody>{quotes.data.slice(0, 12).map((q) => (
              <tr key={q.id}>
                <td style={cellStyle} className="num">{q.items.length} 項</td>
                <td style={cellStyle} className="num">{ntd(q.totalCents)}</td>
                <td style={cellStyle}><Tag tone={QSTATUS[q.status] ?? 'default'}>{q.status}</Tag></td>
                <td style={cellStyle} className="num">{q.createdAt.slice(5, 10)}</td>
                <td style={cellStyle}>
                  <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setQuote.mutate({ id: q.id, status: 'sent' })}>送出</button>
                  <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setQuote.mutate({ id: q.id, status: 'accepted' })}>成交</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </Card>

      {/* 會員訂閱 */}
      <Card title="會員訂閱制" icon="💳">
        {isManager && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--line-soft)' }}>
            <Field label="方案名稱"><input style={{ ...inputStyle, width: 150 }} value={pName} onChange={(e) => setPName(e.target.value)} placeholder="VIP 月費" /></Field>
            <Field label="月費 NT$"><input type="number" style={{ ...inputStyle, width: 100 }} value={pPrice || ''} onChange={(e) => setPPrice(Number(e.target.value))} /></Field>
            <Field label="週期(天)"><input type="number" style={{ ...inputStyle, width: 80 }} value={pInterval} onChange={(e) => setPInterval(Number(e.target.value))} /></Field>
            <Field label="權益"><input style={{ ...inputStyle, width: 180 }} value={pBenefits} onChange={(e) => setPBenefits(e.target.value)} placeholder="每月一次保養 + 9 折" /></Field>
            <button className="btn btn-primary" disabled={!pName || !pPrice || addPlan.isPending} onClick={() => addPlan.mutate()}>新增方案</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <div style={{ width: 230 }}><CustomerPicker value={sCust} onChange={setSCust} /></div>
          <Select value={sPlan} onChange={setSPlan} placeholder="選方案…" options={(plans.data ?? []).map((p) => ({ value: p.id, label: `${p.name}(${ntd(p.priceCents)}/${p.intervalDays}天)` }))} />
          <button className="btn btn-primary" disabled={!sCust || !sPlan || subscribe.isPending} onClick={() => subscribe.mutate()}>開通訂閱</button>
        </div>
        {!subs.data?.length ? <Empty>尚無訂閱。</Empty> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>方案</th><th style={thStyle}>狀態</th><th style={thStyle}>下次扣款</th><th style={thStyle}></th></tr></thead>
            <tbody>{subs.data.slice(0, 12).map((s) => (
              <tr key={s.id}>
                <td style={cellStyle}>{plans.data?.find((p) => p.id === s.planId)?.name ?? '—'}</td>
                <td style={cellStyle}><Tag tone={s.status === 'active' ? 'success' : s.status === 'cancelled' ? 'danger' : 'warn'}>{s.status}</Tag></td>
                <td style={cellStyle} className="num">{s.nextBillingAt ? dt(s.nextBillingAt) : '—'}</td>
                <td style={cellStyle}>{s.status === 'active' && <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setSub.mutate({ id: s.id, status: 'cancelled' })}>取消</button>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 8 }}>※ 自動續扣需串接金流;目前為訂閱管理與下次扣款日排程,實際扣款待金流上線。</div>
      </Card>
    </div>
  );
}
